import { useMemo, useState, useEffect } from "react";
import { getStoredPageSize, setStoredPageSize } from "@/components/Pagination";
import { Edit, Trash2, AlertTriangle, ClipboardCheck, MoreHorizontal, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { SortDescriptor, Selection } from "react-aria-components";
import { Table, TableCard, TableRowActionsDropdown } from "@/components/application/table/table-fixed";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

interface TeamPageTableProps {
    members?: any[];
    setMembers?: (members: any[]) => void;
    onDeleteMembers?: (members: any[]) => Promise<boolean>;
    onEditMember?: (member: any) => void;
    onManageRequirements?: (member: any) => void;
    projectId?: string;
    loading?: boolean;
}

// Generate skeleton placeholder items for loading state
const skeletonItems = Array.from({ length: 5 }, (_, i) => ({
    id: `skeleton-${i}`,
    name: '',
    email: '',
    job_title: '',
    status: 'active',
    sites: 0,
    requirements: { completed: 0, total: 0 },
    compliance: 0,
    isLoading: true,
}));

export const TeamPageTable = ({ members, setMembers, onDeleteMembers, onEditMember, onManageRequirements, projectId, loading = false }: TeamPageTableProps) => {
    const navigate = useNavigate();
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: "name",
        direction: "ascending",
    });
    const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());
    const [items, setItems] = useState(members || []);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<'bulk' | string>('bulk');
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(() => getStoredPageSize(10));

    // Update items when members prop changes
    useEffect(() => {
        if (members) {
            setItems(members);
            // Don't reset sort descriptor when members change - preserve user's sort preference
        }
    }, [members]);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            let first = a[sortDescriptor.column as keyof typeof a];
            let second = b[sortDescriptor.column as keyof typeof b];

            // Special handling for requirements column - sort by total
            if (sortDescriptor.column === "requirements") {
                first = first?.total ?? 0;
                second = second?.total ?? 0;
            }

            // Special handling for sites column - default to 1 if missing
            if (sortDescriptor.column === "sites") {
                first = first ?? 1;
                second = second ?? 1;
            }

            // Handle null/undefined values - always sort them to the end
            if (first == null && second == null) return 0;
            if (first == null) return 1;
            if (second == null) return -1;

            // Compare numbers or booleans
            if ((typeof first === "number" && typeof second === "number") || (typeof first === "boolean" && typeof second === "boolean")) {
                return sortDescriptor.direction === "descending" ? Number(second) - Number(first) : Number(first) - Number(second);
            }

            // Compare strings
            if (typeof first === "string" && typeof second === "string") {
                let cmp = first.toLowerCase().localeCompare(second.toLowerCase());
                if (sortDescriptor.direction === "descending") {
                    cmp *= -1;
                }
                return cmp;
            }

            return 0;
        });
    }, [sortDescriptor, items]);

    // Pagination calculations
    const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = sortedItems.slice(startIndex, endIndex);

    // Reset to page 1 when items change
    useEffect(() => {
        setCurrentPage(1);
    }, [items.length]);

    // Get selected items count and handle selection
    const selectedCount = selectedKeys === "all" ? items.length : selectedKeys.size;
    const selectedItemsList = useMemo(() => {
        if (selectedKeys === "all") return items;
        return items.filter(item => selectedKeys.has(item.id || item.username));
    }, [selectedKeys, items]);

    // Handle delete (bulk or individual)
    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            let membersToDelete: any[];
            
            if (deleteTarget === 'bulk') {
                // Bulk delete
                if (selectedKeys === "all") {
                    membersToDelete = items;
                } else {
                    membersToDelete = items.filter(item => 
                        selectedKeys.has(item.id || item.username)
                    );
                }
            } else {
                // Individual delete
                membersToDelete = items.filter(item => 
                    item.username === deleteTarget || item.id === deleteTarget
                );
            }
            
            // Call the Supabase delete handler if provided
            if (onDeleteMembers && membersToDelete.length > 0) {
                const success = await onDeleteMembers(membersToDelete);
                if (success) {
                    toast.success(`Successfully removed ${membersToDelete.length} member(s)`);
                    // Clear selection after successful bulk delete
                    if (deleteTarget === 'bulk') {
                        setSelectedKeys(new Set());
                    }
                } else {
                    toast.error('Failed to remove member(s)');
                }
            } else {
                // Fallback to local deletion if no handler provided
                let newItems: any[];
                if (deleteTarget === 'bulk') {
                    if (selectedKeys === "all") {
                        newItems = [];
                    } else {
                        newItems = items.filter(item => 
                            !selectedKeys.has(item.username) && !selectedKeys.has(item.id)
                        );
                    }
                    setSelectedKeys(new Set());
                } else {
                    newItems = items.filter(item => 
                        item.username !== deleteTarget && item.id !== deleteTarget
                    );
                }
                setItems(newItems);
                if (setMembers) {
                    setMembers(newItems);
                }
            }
        } catch (error) {
            console.error('Error deleting members:', error);
            toast.error('An error occurred while deleting');
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
        }
    };

    // Handle individual delete - uses member id as identifier
    const handleIndividualDelete = (memberId: string) => {
        setDeleteTarget(memberId);
        setShowDeleteDialog(true);
    };

    // Handle bulk delete
    const handleBulkDelete = () => {
        setDeleteTarget('bulk');
        setShowDeleteDialog(true);
    };

    const getTeamBadgeColor = (color: string) => {
        const colorMap: Record<string, string> = {
            blue: "bg-blue-100 text-blue-700 hover:bg-blue-200",
            green: "bg-green-100 text-green-700 hover:bg-green-200",
            purple: "bg-purple-100 text-purple-700 hover:bg-purple-200",
            orange: "bg-orange-100 text-orange-700 hover:bg-orange-200",
            yellow: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
            pink: "bg-pink-100 text-pink-700 hover:bg-pink-200",
            red: "bg-red-100 text-red-700 hover:bg-red-200",
            indigo: "bg-indigo-100 text-indigo-700 hover:bg-indigo-200",
            teal: "bg-teal-100 text-teal-700 hover:bg-teal-200",
            gray: "bg-gray-100 text-gray-700 hover:bg-gray-200",
            cyan: "bg-cyan-100 text-cyan-700 hover:bg-cyan-200",
            amber: "bg-amber-100 text-amber-700 hover:bg-amber-200",
            slate: "bg-slate-100 text-slate-700 hover:bg-slate-200",
        };
        return colorMap[color] || "bg-gray-100 text-gray-700";
    };

    return (
        <>
            <TableCard.Root>
                <TableCard.Header
                    title="Team members"
                    badge={`${items.length} users`}
                    contentTrailing={
                        <div className="absolute top-5 right-4 md:right-6 flex items-center gap-2">
                            {selectedCount > 0 && (
                                <div className="flex items-center gap-2 mr-4 transition-all duration-200">
                                    <Badge variant="secondary" className="font-medium">
                                        {selectedCount} selected
                                    </Badge>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={handleBulkDelete}
                                        className="h-8"
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Remove Selected
                                    </Button>
                                </div>
                            )}
                        </div>
                    }
                />
                <Table
                    aria-label="Team members"
                    selectionMode={loading ? undefined : "multiple"}
                    selectionBehavior={loading ? undefined : "toggle"}
                    selectedKeys={selectedKeys}
                    onSelectionChange={setSelectedKeys}
                    sortDescriptor={sortDescriptor}
                    onSortChange={setSortDescriptor}
                >
                    <Table.Header>
                        <Table.Head id="name" label="Employee" isRowHeader allowsSorting={!loading} className="w-[22%]" />
                        <Table.Head id="job_title" label="Job Title" allowsSorting={!loading} className="w-[14%]" />
                        <Table.Head id="status" label="Status" allowsSorting={!loading} className="w-[10%]" />
                        <Table.Head id="sites" label="Sites" allowsSorting={!loading} tooltip="Number of spaces this user exists within" className="w-[6%]" />
                        <Table.Head id="requirements" label="Reqs" allowsSorting={!loading} tooltip="Requirement completion status" className="w-[12%]" />
                        <Table.Head id="compliance" label="Compliance" allowsSorting={!loading} tooltip="Overall compliance percentage" className="w-[16%]" />
                        <Table.Head id="actions" label="" className="w-[6%]" />
                    </Table.Header>

                    <Table.Body items={loading ? skeletonItems : paginatedItems}>
                            {(item: any) => (
                                <Table.Row id={item.id || item.username}>
                                    <Table.Cell>
                                        {item.isLoading ? (
                                            <div className="flex items-center gap-2">
                                                <Skeleton className="h-8 w-8 rounded-full" />
                                                <div className="space-y-1.5">
                                                    <Skeleton className="h-4 w-28" />
                                                    <Skeleton className="h-3 w-36" />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 min-w-0">
                                                <Avatar className="h-8 w-8 flex-shrink-0">
                                                    <AvatarImage src={item.avatarUrl || item.avatar_url} alt={item.name} />
                                                    <AvatarFallback className="text-xs">
                                                        {item.name.split(' ').map((n: string) => n[0]).join('')}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <p className="text-sm font-medium text-gray-900 truncate" title={item.name}>{item.name}</p>
                                                        {(item.company_role === "Admin" || item.company_role === "Manager") && (
                                                            <Badge
                                                                variant={item.company_role === "Admin" ? "default" : "secondary"}
                                                                className="text-[9px] px-1 py-0 flex-shrink-0"
                                                            >
                                                                {item.company_role === "Admin" ? "A" : "M"}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 truncate" title={item.email}>{item.email}</p>
                                                </div>
                                            </div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {item.isLoading ? (
                                            <Skeleton className="h-4 w-20" />
                                        ) : (
                                            <span className="truncate block text-sm" title={item.job_title || "-"}>
                                                {item.job_title || "-"}
                                            </span>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {item.isLoading ? (
                                            <Skeleton className="h-5 w-14 rounded-full" />
                                        ) : (
                                            <Badge
                                                variant={item.status === "active" ? "default" : "secondary"}
                                                className={item.status === "active" ?
                                                    "bg-green-100 text-green-800 hover:bg-green-200" :
                                                    "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                                }
                                            >
                                                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                                                    item.status === "active" ? "bg-green-600" : "bg-gray-600"
                                                }`} />
                                                {item.status === "active" ? "Active" : "Inactive"}
                                            </Badge>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {item.isLoading ? (
                                            <Skeleton className="h-5 w-6 rounded" />
                                        ) : (
                                            <Badge variant="secondary" className="text-xs">
                                                {item.sites || 1}
                                            </Badge>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {item.isLoading ? (
                                            <div className="flex gap-0.5">
                                                <Skeleton className="h-5 w-6 rounded" />
                                                <Skeleton className="h-5 w-6 rounded" />
                                            </div>
                                        ) : (
                                            <div
                                                className="flex items-center gap-1 group cursor-pointer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onManageRequirements && onManageRequirements(item);
                                                }}
                                            >
                                                {item.requirements && item.requirements.total > 0 ? (
                                                    <div className="flex items-center gap-0.5">
                                                        {item.requirements.completed > 0 && (
                                                            <Badge
                                                                variant="outline"
                                                                className="border-green-200 bg-green-50 text-green-700 px-1.5 py-0 text-[10px] group-hover:border-green-300 transition-colors pointer-events-none"
                                                            >
                                                                {item.requirements.completed}
                                                            </Badge>
                                                        )}
                                                        {item.requirements.pending > 0 && (
                                                            <Badge
                                                                variant="outline"
                                                                className="border-amber-200 bg-amber-50 text-amber-700 px-1.5 py-0 text-[10px] group-hover:border-amber-300 transition-colors pointer-events-none"
                                                            >
                                                                {item.requirements.pending}
                                                            </Badge>
                                                        )}
                                                        {item.requirements.expired > 0 && (
                                                            <Badge
                                                                variant="outline"
                                                                className="border-red-200 bg-red-50 text-red-700 px-1.5 py-0 text-[10px] group-hover:border-red-300 transition-colors pointer-events-none"
                                                            >
                                                                {item.requirements.expired}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] text-gray-400">None</span>
                                                )}
                                            </div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {item.isLoading ? (
                                            <div className="space-y-1">
                                                <div className="flex justify-between">
                                                    <Skeleton className="h-3 w-8" />
                                                    <Skeleton className="h-3 w-8" />
                                                </div>
                                                <Skeleton className="h-1.5 w-full rounded-full" />
                                            </div>
                                        ) : (
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div className="w-full cursor-pointer">
                                                            <div className="flex items-center justify-between mb-0.5">
                                                                <span className="text-xs font-medium text-gray-600">
                                                                    {item.compliance || 0}%
                                                                </span>
                                                                <span className="text-[10px] text-gray-500">
                                                                    {(item.valid || 0) + (item.expiring || 0)}/{item.assigned_count || 0}
                                                                </span>
                                                            </div>
                                                            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden flex">
                                                                {/* Valid segment - green */}
                                                                {(item.valid || 0) > 0 && (item.assigned_count || 0) > 0 && (
                                                                    <div
                                                                        className="h-full bg-green-500 transition-all duration-300"
                                                                        style={{ width: `${((item.valid || 0) / (item.assigned_count || 1)) * 100}%` }}
                                                                    />
                                                                )}
                                                                {/* Expiring segment - amber */}
                                                                {(item.expiring || 0) > 0 && (item.assigned_count || 0) > 0 && (
                                                                    <div
                                                                        className="h-full bg-amber-500 transition-all duration-300"
                                                                        style={{ width: `${((item.expiring || 0) / (item.assigned_count || 1)) * 100}%` }}
                                                                    />
                                                                )}
                                                                {/* Expired segment - red */}
                                                                {(item.expired || 0) > 0 && (item.assigned_count || 0) > 0 && (
                                                                    <div
                                                                        className="h-full bg-red-500 transition-all duration-300"
                                                                        style={{ width: `${((item.expired || 0) / (item.assigned_count || 1)) * 100}%` }}
                                                                    />
                                                                )}
                                                                {/* Missing segment - red */}
                                                                {(item.missing || 0) > 0 && (item.assigned_count || 0) > 0 && (
                                                                    <div
                                                                        className="h-full bg-red-500 transition-all duration-300"
                                                                        style={{ width: `${((item.missing || 0) / (item.assigned_count || 1)) * 100}%` }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <div className="space-y-1 text-xs">
                                                            <div className="font-semibold border-b pb-1 mb-1">Compliance Breakdown</div>
                                                            <div className="flex justify-between gap-4">
                                                                <span className="flex items-center gap-1 text-green-400">
                                                                    <span className="inline-block w-3 text-center">✓</span>
                                                                    <span>Valid:</span>
                                                                </span>
                                                                <span className="font-medium">{(item.valid || 0) + (item.valid_cross_space || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span className="flex items-center gap-1 text-yellow-400">
                                                                    <span className="inline-block w-3 text-center">⚠</span>
                                                                    <span>Expiring:</span>
                                                                </span>
                                                                <span className="font-medium">{(item.expiring || 0) + (item.expiring_cross_space || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span className="flex items-center gap-1 text-red-400">
                                                                    <span className="inline-block w-3 text-center">✗</span>
                                                                    <span>Expired:</span>
                                                                </span>
                                                                <span className="font-medium">{item.expired || 0}</span>
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span className="flex items-center gap-1 text-orange-400">
                                                                    <span className="inline-block w-3 text-center">○</span>
                                                                    <span>Missing:</span>
                                                                </span>
                                                                <span className="font-medium">{item.missing || 0}</span>
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span className="text-gray-400 text-[10px]">
                                                                    Assigned: {item.assigned_count || 0}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell className="px-4" onPointerDown={(e) => e.stopPropagation()}>
                                        {item.isLoading ? (
                                            <div className="flex justify-end">
                                                <Skeleton className="h-8 w-8 rounded" />
                                            </div>
                                        ) : (
                                            <div className="flex justify-end" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 w-8 p-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                        >
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onManageRequirements && onManageRequirements(item);
                                                            }}
                                                        >
                                                            <ClipboardCheck className="h-4 w-4 mr-2" />
                                                            Manage Requirements
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onEditMember && onEditMember(item);
                                                            }}
                                                        >
                                                            <Edit className="h-4 w-4 mr-2" />
                                                            Edit Member
                                                        </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/space/${projectId}/matrix`, {
                                                                state: { viewMode: 'employees', memberId: item.id }
                                                            });
                                                        }}
                                                    >
                                                        <Eye className="h-4 w-4 mr-2" />
                                                        View on Matrix
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleIndividualDelete(item.id || item.username);
                                                        }}
                                                        className="text-red-600 focus:text-red-600"
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                        Remove Member
                                                    </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>

                <div className="px-4 py-3 md:px-6 md:pt-3 md:pb-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4">
                            {selectedCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedKeys(new Set())}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    Clear selection
                                </Button>
                            )}
                            <span className="text-gray-700">
                                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                                <span className="font-medium">{Math.min(endIndex, sortedItems.length)}</span> of{' '}
                                <span className="font-medium">{sortedItems.length}</span> results
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Items per page selector */}
                            <div className="flex items-center gap-2">
                                <span className="text-gray-700">Per page:</span>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        setItemsPerPage(val);
                                        setCurrentPage(1);
                                        setStoredPageSize(val);
                                    }}
                                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>

                            {/* Page navigation */}
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    First
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>

                                {/* Page numbers */}
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(page => {
                                            return (
                                                page === 1 ||
                                                page === totalPages ||
                                                (page >= currentPage - 1 && page <= currentPage + 1)
                                            );
                                        })
                                        .map((page, index, array) => {
                                            const prevPage = array[index - 1];
                                            const showEllipsis = prevPage && page - prevPage > 1;

                                            return (
                                                <span key={page} className="contents">
                                                    {showEllipsis && (
                                                        <span className="px-2 text-gray-500">...</span>
                                                    )}
                                                    <Button
                                                        variant={currentPage === page ? 'default' : 'outline'}
                                                        size="sm"
                                                        onClick={() => setCurrentPage(page)}
                                                        className="w-9"
                                                    >
                                                        {page}
                                                    </Button>
                                                </span>
                                            );
                                        })}
                                </div>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </TableCard.Root>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent className="p-0 overflow-hidden">
                    <div className="p-6 space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <AlertDialogTitle className="text-lg font-semibold">
                                    Remove Team Member{deleteTarget === 'bulk' && selectedCount !== 1 ? 's' : ''}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    This action cannot be undone.
                                </AlertDialogDescription>
                            </div>
                        </div>
                        <div className="rounded-lg bg-muted p-4">
                            <p className="text-sm text-muted-foreground">
                                {deleteTarget === 'bulk' ? (
                                    <>Are you sure you want to remove <span className="font-medium text-foreground">{selectedCount}</span> team member{selectedCount !== 1 ? 's' : ''} from this space?</>
                                ) : (() => {
                                    const member = items.find(i => i.id === deleteTarget);
                                    const displayName = member?.name || member?.email || 'this member';
                                    return <>Are you sure you want to remove <span className="font-medium text-foreground">"{displayName}"</span> from this space?</>;
                                })()}
                            </p>
                        </div>
                        <AlertDialogFooter className="gap-3 pt-2">
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDelete}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deleteTarget === 'bulk'
                                    ? `Remove ${selectedCount} member${selectedCount !== 1 ? 's' : ''}`
                                    : 'Remove member'
                                }
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </div>
                </AlertDialogContent>
            </AlertDialog>

        </>
    );
};